import { Box, LinearProgress } from "@mui/material"
import React from "react"
import type { FieldValues, Path } from "react-hook-form"
import { Controller, useFormContext } from "react-hook-form"

import { IMAGE_MIME_TYPES } from "@/components/upload/constants"
import { SingleFileUpload } from "@/components/upload/SingleFileUpload"
import { convertMimeTypesToAccept, isFileTypeAllowed } from "@/components/upload/utils"
import { FIELD_ERROR_CLASSES, FIELD_LABEL_CLASSES } from "@/constants/forms"

const ALLOWED_FILE_SIZE = 10 * 1024 * 1024 // 10MB
const MAX_FILE_NAME_LENGTH = 64 // matches backend `file.label` varchar(64) constraint

interface FileInputProps<T extends FieldValues> {
  title?: string
  required?: boolean
  name: Path<T>
  fileData?: File
  className?: string
  onChange?: () => void
  fileFetching?: boolean
  allowedMimeTypes?: string[]
  allowedTypesLabel?: string
  uploadAreaTitle?: string
  disabled?: boolean
  "data-testid"?: string
}

// TODO: Consider renaming as FileField as it container label, input, progress and error message.
const FileInput = <T extends FieldValues>({
  title,
  required = false,
  name,
  className,
  fileData,
  fileFetching = false,
  allowedMimeTypes = IMAGE_MIME_TYPES,
  allowedTypesLabel = "jpg, png, gif, jpeg",
  onChange,
  uploadAreaTitle = "Click to upload",
  disabled = false,
  "data-testid": testId = "file-field",
}: FileInputProps<T>): React.ReactElement | null => {
  const { control, resetField, setValue, setError } = useFormContext<T>()

  const validateFileUpload = (file: File | null) => {
    if (file && file?.size >= ALLOWED_FILE_SIZE) {
      setError(name, {
        type: "fileSize",
        message: "Max allowed file size 10MB",
      })
      return false
    }

    if (file && !isFileTypeAllowed(file, allowedMimeTypes)) {
      setError(name, {
        type: "fileType",
        message: `Allowed file type should be ${allowedTypesLabel}`,
      })
      return false
    }

    if (file && file.name.length > MAX_FILE_NAME_LENGTH) {
      setError(name, {
        type: "fileNameLength",
        message: `File name must not exceed ${MAX_FILE_NAME_LENGTH} characters`,
      })
      return false
    }

    return true
  }

  const handleFileChange = (newFile: File | null) => {
    resetField(name)
    if (validateFileUpload(newFile)) {
      setValue(name, (newFile ?? undefined) as any, { shouldDirty: true, shouldValidate: true })
      onChange?.()
    }
  }

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <Box className={className} data-testid={testId}>
          {title && (
            <label htmlFor={name} className={FIELD_LABEL_CLASSES} data-testid={`${testId}-label`}>
              {title}
              {required && <span className='text-red-600'>*</span>}
            </label>
          )}
          {fileFetching ? (
            <LinearProgress className='my-10' data-testid={`${testId}-progress`} />
          ) : (
            <>
              <SingleFileUpload
                accept={convertMimeTypesToAccept(allowedMimeTypes)}
                title={uploadAreaTitle}
                subtitle={`${allowedTypesLabel} (max. 10MB)`}
                onChange={handleFileChange}
                file={fileData || field.value}
                readOnly={disabled}
                error={!!error}
                data-testid={`${testId}-upload`}
              />
              {error?.message && (
                <p className={FIELD_ERROR_CLASSES} data-testid={`${testId}-error`}>
                  {error.message}
                </p>
              )}
            </>
          )}
        </Box>
      )}
    />
  )
}

export default FileInput
